import React, { useMemo, useCallback } from 'react';
import { Box, Tooltip, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import Table from '../../../../components/tables/Table';
import tableStyles from '../../../../components/tables/Table.styles';
import styles from './MeterChangeTable.styles';

import {
  prettifyHeader,
  issuesForHeader,
  parseUKDate,
} from '../../utils/meterChange.helpers';

const HEAD_CELLS = [
  'ReqType',
  'Unit',
  'EVN',
  'OldMeter',
  'NewMeter',
  'NewEFDT',
  'OldETDT',
  'Comments',
];

function FieldIssuesList({ issues = [] }) {
  return (
    <Box sx={styles.issueListBox}>
      <ul style={styles.issueUl}>
        {issues.map((msg, i) => (
          <li key={i} style={styles.issueLi}>
            <Typography sx={{ color: '#B42318', fontSize: '0.85rem', lineHeight: 1.2 }}>
              {msg}
            </Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
}

export default function MeterChangeTable({
  rows = [],
  onRemove,
}) {
  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const getRowId = useCallback(
    (r, i) => r?.ID ?? r?.FileIncomingID ?? r?._originalIndex ?? String(i),
    []
  );

  const columns = useMemo(() => {
    return HEAD_CELLS.map((header) => {
      const field = header;
      const label = prettifyHeader(header);

      const common = {
        field,
        label,
        sortable: true,
        align: 'left',
        cellSx: header === 'Comments'
          ? { position: 'relative', whiteSpace: 'normal', wordBreak: 'break-word' }
          : { position: 'relative' },
      };

      switch (header) {
        case 'NewEFDT':
        case 'OldETDT':
          return {
            ...common,
            render: (row) => {
              const raw = row?.[field];
              if (!raw) return '—';
              const d = parseUKDate(raw);
              return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
            },
          };

        case 'Comments':
          return {
            ...common,
            render: (row) => (row?.Comments ? String(row.Comments) : '—'),
          };

        default:
          return {
            ...common,
            render: (row) => {
              const raw = row?.[field];
              return raw ?? raw === 0 ? String(raw) : '—';
            },
          };
      }
    });
  }, []);

  const columnsWithValidation = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      render: (row, rawValue) => {
        const issues = Array.isArray(row?.issues) ? row.issues.filter(Boolean) : [];
        const hasIssues = row?.Validated === false && issues.length > 0;

        const cellHeader = col.field;
        const relatedErrors = hasIssues ? issuesForHeader(cellHeader, issues) : [];

        const baseValue = col.render ? col.render(row, rawValue) : rawValue;

        if (hasIssues && relatedErrors.length > 0) {
          return (
            <Tooltip
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#fff',
                    color: 'inherit',
                    boxShadow: 1,
                    px: 0.5,
                    py: 0.5,
                    fontSize: '0.85rem',
                    maxWidth: 260,
                  },
                },
                arrow: { sx: { color: '#fff' } },
              }}
              title={<FieldIssuesList issues={relatedErrors} />}
              arrow
              placement="bottom-start"
            >
              <Box sx={styles.invalidCellFull} role="presentation">
                <Typography sx={styles.errorCellText}>
                  {baseValue}
                </Typography>
              </Box>
            </Tooltip>
          );
        }

        return baseValue;
      },
    }));
  }, [columns]);

  const actionsRenderer = (row) => (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
      <IconButton
        size="small"
        onClick={() => onRemove?.(safeRows.indexOf(row))}
        sx={styles.compactActionButton}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const rowSx = (row) => {
    const issues = Array.isArray(row?.issues) ? row.issues.filter(Boolean) : [];
    const hasIssues = row?.Validated === false && issues.length > 0;
    return hasIssues ? styles.invalidRowStrongRow : undefined;
  };

  return (
    <Box sx={{ ...tableStyles.tableWrapper, flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Table
        rows={safeRows}
        columns={columnsWithValidation}
        actionsRenderer={actionsRenderer}
        actionsHeader={null}
        stickyHeader
        zebra
        hover
        bordered
        getRowId={getRowId}

        defaultSort={{ field: null, direction: 'asc' }}
        onSortChange={() => {}}
        rowSx={rowSx}
      />
    </Box>
  );
}